import { useEffect, type RefObject } from "react";
import type { MenuListEntry } from "@/components/Menu/Menu";
import type { MdMenu } from "@/components/Menu/LitMenu";
import { MENU_ITEM_KEYBOARD_FOCUS_CLASS } from "@/components/Menu/menuStyles";
import { isEditableKeyboardTarget } from "@/lib/keyboardShortcuts";

function isMenuHeadline(item: MenuListEntry): item is Extract<MenuListEntry, { type: "headline" }> {
  return item.type === "headline";
}

function getNavigableMenuItemElements(menu: MdMenu): HTMLElement[] {
  const candidates: HTMLElement[] = [];

  if (!menu || typeof menu !== "object" || !("children" in menu)) return candidates;

  const walk = (root: Element) => {
    const children = root.children;
    if (!children) return;

    for (const child of children) {
      const tag = child.tagName.toLowerCase();

      if (tag === "md-menu-item") {
        if (!child.hasAttribute("disabled")) {
          candidates.push(child as HTMLElement);
        }
        continue;
      }

      if (
        tag === "button" &&
        child.getAttribute("role") === "menuitem" &&
        !child.hasAttribute("disabled") &&
        child.getAttribute("aria-disabled") !== "true"
      ) {
        candidates.push(child as HTMLElement);
        continue;
      }

      if (child.children.length > 0) {
        walk(child);
      }
    }
  };

  walk(menu);
  return candidates;
}

function getActiveNavigableMenuItemIndex(items: HTMLElement[]): number {
  const active = document.activeElement;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item === active || item.contains(active)) return i;
    if (item.tabIndex === 0) return i;
  }

  return -1;
}

function clearMenuKeyboardFocus(menu: MdMenu) {
  for (const element of getNavigableMenuItemElements(menu)) {
    element.tabIndex = -1;
    element.classList.remove(MENU_ITEM_KEYBOARD_FOCUS_CLASS);
  }
}

function setMenuItemActive(item: HTMLElement, menu: MdMenu) {
  clearMenuKeyboardFocus(menu);

  item.tabIndex = 0;
  item.classList.add(MENU_ITEM_KEYBOARD_FOCUS_CLASS);
  item.focus();
}

function focusMenuItemAtIndex(menu: MdMenu, index: number) {
  const items = getNavigableMenuItemElements(menu);
  const item = items[index];
  if (!item) return;
  setMenuItemActive(item, menu);
}

function focusFirstMenuItem(menu: MdMenu) {
  focusMenuItemAtIndex(menu, 0);
}

function navigateMenuItem(menu: MdMenu, direction: "next" | "previous") {
  const items = getNavigableMenuItemElements(menu);
  if (items.length === 0) return;

  const activeIndex = getActiveNavigableMenuItemIndex(items);
  const nextIndex =
    activeIndex === -1
      ? direction === "next"
        ? 0
        : items.length - 1
      : direction === "next"
        ? (activeIndex + 1) % items.length
        : (activeIndex - 1 + items.length) % items.length;

  setMenuItemActive(items[nextIndex], menu);
}

function getActiveMenuItemElement(menu: MdMenu): HTMLElement | null {
  const items = getNavigableMenuItemElements(menu);
  const activeIndex = getActiveNavigableMenuItemIndex(items);
  if (activeIndex >= 0) return items[activeIndex];

  if (typeof menu.querySelector !== "function") return null;

  return menu.querySelector("md-menu-item[tabindex='0'], button[role='menuitem'][tabindex='0']") as HTMLElement | null;
}

function activateMenuItemByTestId(menuItems: MenuListEntry[], dataTestId: string, onClose: () => void): boolean {
  for (const item of menuItems) {
    if (isMenuHeadline(item)) continue;
    if (item.disabled || item.nonInteractive) continue;
    if (item.dataTestId !== dataTestId) continue;
    if (!item.onClick) continue;

    item.onClick();
    onClose();
    return true;
  }

  return false;
}

/**
 * While a menu is open, single-key chords (e.g. `i`, `n`, `t`) activate matching items by `chordKey`.
 * On desktop, arrow keys and Tab move focus between items; Enter/Space activate the focused row.
 * Vertical menus use Up/Down; horizontal navbar menus use Left/Right.
 */
export default function useMenuChordKeys({
  open,
  menuItems,
  onClose,
  menuRef,
  enableArrowNavigation = false,
  arrowNavigationAxis = "vertical"
}: {
  open: boolean;
  menuItems: MenuListEntry[];
  onClose: () => void;
  menuRef?: RefObject<MdMenu | null>;
  enableArrowNavigation?: boolean;
  arrowNavigationAxis?: "vertical" | "horizontal";
}) {
  useEffect(() => {
    if (!open) return;

    const active = document.activeElement;
    if (isEditableKeyboardTarget(active)) {
      (active as HTMLElement).blur();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !menuRef?.current) return;

    const menu = menuRef.current;
    const frame = requestAnimationFrame(() => {
      focusFirstMenuItem(menu);
    });

    return () => cancelAnimationFrame(frame);
  }, [open, menuRef]);

  useEffect(() => {
    if (open || !menuRef?.current) return;
    clearMenuKeyboardFocus(menuRef.current);
  }, [open, menuRef]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;

      const menu = menuRef?.current;

      if (enableArrowNavigation && menu) {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onClose();
          return;
        }

        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          navigateMenuItem(menu, event.shiftKey ? "previous" : "next");
          return;
        }

        if (arrowNavigationAxis === "horizontal") {
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            event.stopPropagation();
            navigateMenuItem(menu, event.key === "ArrowRight" ? "next" : "previous");
            return;
          }

          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        } else {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            event.stopPropagation();
            navigateMenuItem(menu, event.key === "ArrowDown" ? "next" : "previous");
            return;
          }

          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        }

        if (event.key === "Enter" || event.key === " ") {
          const activeElement = getActiveMenuItemElement(menu);
          const dataTestId = activeElement?.getAttribute("data-testid");
          if (dataTestId && activateMenuItemByTestId(menuItems, dataTestId, onClose)) {
            event.preventDefault();
            event.stopPropagation();
          }
          return;
        }
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();

      for (const item of menuItems) {
        if (isMenuHeadline(item)) continue;
        if (item.disabled || item.nonInteractive) continue;
        if (!item.chordKey || item.chordKey.toLowerCase() !== key) continue;
        if (!item.onClick) continue;

        event.preventDefault();
        item.onClick();
        onClose();
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, menuItems, onClose, menuRef, enableArrowNavigation, arrowNavigationAxis]);
}
